import { useEffect, useRef } from "react";

type Tick = () => void | Promise<void>;

interface Options {
  /**
   * Optional callback fired ONLY on the first mount and on
   * visibility-change-to-visible (i.e. user actions, not interval ticks).
   * Defaults to `tick` when omitted. Pass a separate fetcher when the
   * "user-driven" path should differ from the interval poll — e.g. for
   * HH-200 Unit 28 where intervals must NOT extend the sliding idle
   * window but mount/return-to-tab still should.
   */
  initialTick?: Tick;
}

/**
 * Run a `tick` on a recurring `intervalMs` schedule — **only while
 * `document.visibilityState === "visible"`**. When the tab is hidden
 * the interval pauses; when it returns to visible we fire `initialTick`
 * (or `tick`) once to catch up and resume.
 *
 * If `intervalMs` changes while mounted, the previous timer is torn
 * down and a fresh one is started **without** an extra immediate tick.
 *
 * Background story (HH-200 Unit 28): the admin-portal previously used
 * naked `setInterval` for badge / unread-count polling. Browsers throttle
 * but don't stop background timers, so polls kept hitting authenticated
 * endpoints and extended the backend's sliding idle window — sessions
 * never expired while users were on another tab. Pausing on hide
 * (combined with the backend's `X-Activity: background` opt-out) makes
 * the idle timeout actually behave like an idle timeout.
 */
export function useVisibilityAwareInterval(
  tick: Tick,
  intervalMs: number,
  options: Options = {},
): void {
  // Hold the latest callbacks in refs so the effect doesn't re-run when
  // callers pass inline functions (a common pattern).
  const tickRef = useRef(tick);
  const initialTickRef = useRef<Tick>(options.initialTick ?? tick);
  useEffect(() => {
    tickRef.current = tick;
    initialTickRef.current = options.initialTick ?? tick;
  }, [tick, options.initialTick]);

  // Distinguish initial mount from intervalMs-change re-runs so we only
  // fire the immediate tick once per component lifetime.
  const isInitialMountRef = useRef(true);

  useEffect(() => {
    let timer: ReturnType<typeof setInterval> | null = null;
    const isInitialMount = isInitialMountRef.current;
    isInitialMountRef.current = false;

    // Wrap each tick so a rejected promise from an async callback can't
    // surface as an unhandled rejection. Polling is best-effort.
    const fire = (cb: Tick) => {
      Promise.resolve(cb()).catch(() => {
        // intentional swallow
      });
    };

    const start = (skipImmediate = false) => {
      if (timer !== null) return;
      // Initial mount + visibility-return treated as user-driven activity:
      // we use the initialTick (foreground in HH-200 terms). Every
      // interval tick after that uses the regular `tick` (background).
      if (!skipImmediate) fire(initialTickRef.current);
      timer = setInterval(() => fire(tickRef.current), intervalMs);
    };

    const stop = () => {
      if (timer === null) return;
      clearInterval(timer);
      timer = null;
    };

    const onVis = () => {
      if (document.visibilityState === "visible") start();
      else stop();
    };

    if (typeof document !== "undefined" && !document.hidden) {
      // Tick immediately on the very first mount; on subsequent re-runs
      // (caller changed intervalMs) just rebuild the schedule.
      start(/* skipImmediate */ !isInitialMount);
    }
    document.addEventListener("visibilitychange", onVis);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [intervalMs]);
}
